import styled from "@emotion/styled";
import { logos, socialIcons } from "../../assets/icons";
import {
  breakpoints,
  fontWeight,
  fontSize,
  spacing,
  opacity,
  lineHeight,
  letterSpacing,
  footer,
  text,
} from "@atlas-int/tokens";
import {
  countryProfileRoutes,
  headerLinkMetadata,
} from "../header/Utils";
import { Link } from "react-router";

export const footerColor = footer.bg;
const breakpointSmall = breakpoints.md;

export const CopyrightContainer = styled.div`
  position: relative;
`;

const Root = styled.div`
  background-color: #e6e6e6;
  padding: 7vh 100px 4vh;
  line-height: ${lineHeight.none};
  letter-spacing: ${letterSpacing.wide};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;

  @media (min-width: ${breakpointSmall}) {
    display: grid;
    grid-template-columns: auto auto;
    grid-column-gap: ${spacing[20]};
    justify-content: center;
    align-items: start;
  }
`;

const Title = styled.div`
  margin-bottom: ${spacing[16]};
  font-size: ${fontSize.lg};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${breakpointSmall}) {
    margin-bottom: 0;
    text-align: left;
    align-items: flex-start;
  }
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: ${spacing[16]};

  @media (min-width: ${breakpointSmall}) {
    display: grid;
    grid-template-columns: auto auto;
    align-items: stretch;
    gap: ${spacing[20]};
    margin-bottom: 0;
  }
`;

const LeftColumnLinks = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  margin-bottom: ${spacing[12]};

  @media (min-width: ${breakpointSmall}) {
    align-items: flex-start;
    margin-bottom: 0;
  }
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${breakpointSmall}) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const RightColumnLinks = styled.ul`
  padding: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: ${breakpointSmall}) {
    align-items: flex-start;
  }
`;

const SocialMediaSection = styled.div`
  text-align: center;

  @media (min-width: ${breakpointSmall}) {
    text-align: left;
    margin-bottom: ${spacing[6]};
  }
`;

const SocialMediaHeading = styled.h4`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.medium};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
  text-transform: none;
`;

const SocialMediaLinks = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[6]};
  padding: 0;
  margin: 0;
  flex-wrap: wrap;

  @media (min-width: ${breakpointSmall}) {
    justify-content: flex-start;
  }
`;

const GrowthLabLogo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  & p {
    text-transform: none;
    font-size: ${fontSize.xs};
    line-height: ${lineHeight.normal};
  }
`;
const Li = styled.li`
  margin-bottom: ${spacing[6]};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.light};
  list-style: none;

  a {
    text-decoration: none;
    color: ${text.primary};
    &:hover {
      border-bottom: 1px solid ${text.primary};
    }
  }
`;

const SocialLink = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 0;
  line-height: 0;

  a {
    display: block;
    text-decoration: none;
    border: none;
    font-size: 0;
    color: transparent;
  }

  a:hover {
    border: none;
  }
`;

const SocialIconContainer = styled.span`
  display: block;
  width: ${spacing[12]};
  height: ${spacing[12]};
  filter: opacity(${opacity.muted});

  &:hover {
    filter: opacity(${opacity.loading});
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const AtlasLogoImage = styled.div`
  max-width: 280px;
  margin: auto;

  img {
    width: 100%;
    height: 100%;
  }
`;

const Copyright = styled(CopyrightContainer)`
  padding: ${spacing[4]};
  text-align: center;
  background-color: ${text.primary};
  color: ${text.onDark};
  font-size: ${fontSize.base};

  a {
    color: ${text.onDark};
    text-decoration: none;
    border-bottom: solid 1px transparent;

    &:hover {
      border-bottom-color: ${text.onDark};
    }
  }
`;

const Footer = () => {
  const mailingListLink = `https://hksexeced.tfaforms.net/f/subscribe?s=a1n6g000000nJnxAAE`;

  const linkedInLink = `https://www.linkedin.com/company/harvard-growth-lab/`;

  const instagramLink = `https://www.instagram.com/harvardgrowthlab/`;

  const versionNumber = import.meta.env.REACT_APP_ATLAS_VERSION_NUMBER;

  return (
    <>
      <Root>
        <Title>
          <a
            href="https://growthlab.hks.harvard.edu/home"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AtlasLogoImage>
              <img src={logos.glLogoBlack} alt="The Growth Lab" />
            </AtlasLogoImage>
          </a>
          <GrowthLabLogo>
            <p>
              79 JFK St. | Cambridge, MA 02138
              <br />
              growthlab@hks.harvard.edu
            </p>
          </GrowthLabLogo>
          <p>
            <em>Atlas {versionNumber}</em>
          </p>
        </Title>
        <NavSection>
          <LeftColumnLinks>
            {headerLinkMetadata.map((navigationLink) => {
              if (countryProfileRoutes.includes(navigationLink.routeId)) {
                return (
                  <Li key={navigationLink.routeId}>
                    <a href={navigationLink.route}>{navigationLink.label}</a>
                  </Li>
                );
              } else {
                return (
                  <Li key={navigationLink.routeId}>
                    <Link to={navigationLink.route}>
                      {navigationLink.label}
                    </Link>
                  </Li>
                );
              }
            })}
          </LeftColumnLinks>
          <RightColumnWrapper>
            <RightColumnLinks>
              <Li>
                <a
                  href="mailto:growthlabtools@hks.harvard.edu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                </a>
              </Li>
              <Li>
                <a href={mailingListLink} target="_blank" rel="noopener">
                  Newsletter
                </a>
              </Li>
              <Li>
                <a
                  href="https://growthlab.hks.harvard.edu/jobs-opportunities"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Work with us
                </a>
              </Li>
            </RightColumnLinks>
            <SocialMediaSection>
              <SocialMediaHeading>Connect with us</SocialMediaHeading>
              <SocialMediaLinks>
                <SocialLink>
                  <a
                    href="https://www.facebook.com/harvardgrowthlab/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Facebook"
                  >
                    <SocialIconContainer>
                      <img src={socialIcons.facebook} alt="Facebook" title="Facebook" />
                    </SocialIconContainer>
                    Facebook
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href={linkedInLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                  >
                    <SocialIconContainer>
                      <img src={socialIcons.linkedin} alt="LinkedIn" title="LinkedIn" />
                    </SocialIconContainer>
                    LinkedIn
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href="https://x.com/HarvardGrwthLab"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="X"
                  >
                    <SocialIconContainer>
                      <img src={socialIcons.x} alt="X" title="X" />
                    </SocialIconContainer>
                    X
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href={instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Instagram"
                  >
                    <SocialIconContainer>
                      <img
                        src={socialIcons.instagram}
                        alt="Instagram"
                        title="Instagram"
                      />
                    </SocialIconContainer>
                    Instagram
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href="https://youtube.com/playlist?list=PLVJQsjaKb-4SNl2obPcBFtIbjKoyi4e5z"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="YouTube"
                  >
                    <SocialIconContainer>
                      <img src={socialIcons.youtube} alt="YouTube" title="YouTube" />
                    </SocialIconContainer>
                    YouTube
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href="https://podcasts.apple.com/us/podcast/growth-lab-podcast-series/id1486218164"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Apple Podcast"
                  >
                    <SocialIconContainer>
                      <img
                        src={socialIcons.applepodcast}
                        alt="Apple Podcast"
                        title="Apple Podcast"
                      />
                    </SocialIconContainer>
                    Apple Podcast
                  </a>
                </SocialLink>
                <SocialLink>
                  <a
                    href="https://github.com/harvard-growth-lab"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="GitHub"
                  >
                    <SocialIconContainer>
                      <img src={socialIcons.github} alt="GitHub" title="GitHub" />
                    </SocialIconContainer>
                    GitHub
                  </a>
                </SocialLink>
              </SocialMediaLinks>
            </SocialMediaSection>
          </RightColumnWrapper>
        </NavSection>
      </Root>
      <Copyright>
        Copyright © {new Date().getFullYear()} The President and Fellows of
        Harvard College
        {" | "}
        <a
          href="https://gdpr.harvard.edu/eeaprivacydisclosures"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy
        </a>
        {" | "}
        <a
          href="http://accessibility.harvard.edu/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Accessibility
        </a>
        {" | "}
        <a
          href="https://accessibility.huit.harvard.edu/digital-accessibility-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Digital Accessibility
        </a>
        {" | "}
        <a
          href="http://www.harvard.edu/reporting-copyright-infringements"
          target="_blank"
          rel="noopener noreferrer"
        >
          Report Copyright
        </a>
      </Copyright>
    </>
  );
};

export default Footer;
